// client/src/coms/workspace/usePanelResize.js
// Drag (pointer) or keyboard resizing for a panel width or a split ratio. While dragging only this hook's
// state changes (one update per animation frame); the preference is saved once, when the drag ends.
import { useCallback, useEffect, useRef, useState } from 'react';

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

export default function usePanelResize({ value, min, max, invert = false, ratio = false, vertical = false, onCommit }) {
  const [live, setLive] = useState(null);
  const containerRef = useRef(null);
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const end = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    try {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    } catch {}
    if (d && d.last != null && d.last !== d.startValue && typeof commitRef.current === 'function') commitRef.current(d.last);
    setLive(null);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const pos = vertical ? e.clientY : e.clientX;
      const delta = (pos - d.startPos) * (invert ? -1 : 1);
      d.last = ratio ? clamp(d.startValue + delta / Math.max(1, d.span), min, max) : clamp(Math.round(d.startValue + delta), min, max);
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = 0;
          if (dragRef.current) setLive(dragRef.current.last);
        });
      }
    };
    const onUp = () => {
      if (dragRef.current) end();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [vertical, invert, ratio, min, max, end]);

  const onStart = useCallback(
    (e) => {
      if (!e || (e.button != null && e.button !== 0)) return;
      e.preventDefault();
      let span = 1;
      if (ratio && containerRef.current) {
        const r = containerRef.current.getBoundingClientRect();
        span = vertical ? r.height : r.width;
      }
      dragRef.current = { startPos: vertical ? e.clientY : e.clientX, startValue: value, last: value, span };
      setLive(value);
      try {
        document.body.style.cursor = vertical ? 'row-resize' : 'col-resize';
        document.body.style.userSelect = 'none';
      } catch {}
    },
    [value, ratio, vertical]
  );

  const onKeyDown = useCallback(
    (e) => {
      const step = ratio ? 0.02 : 10;
      const keys = vertical ? { dec: 'ArrowUp', inc: 'ArrowDown' } : { dec: 'ArrowLeft', inc: 'ArrowRight' };
      let next = null;
      if (e.key === keys.inc) next = value + step * (invert ? -1 : 1);
      else if (e.key === keys.dec) next = value - step * (invert ? -1 : 1);
      else if (e.key === 'Home') next = min;
      else if (e.key === 'End') next = max;
      if (next == null) return;
      e.preventDefault();
      next = ratio ? clamp(Math.round(next * 100) / 100, min, max) : clamp(Math.round(next), min, max);
      if (next !== value && typeof commitRef.current === 'function') commitRef.current(next);
    },
    [value, ratio, vertical, invert, min, max]
  );

  return { size: live != null ? live : value, active: live != null, onStart, onKeyDown, containerRef };
}
